const LS_MODULES = 'allowedModules'
const LS_FIELDS = 'allowedFields'
const LS_CAN_MANAGE_ROLES = 'canManageRoles'
const LS_SUPER_ADMIN = 'isSuperAdmin'
const LS_PERMISSIONS_READY = 'permissionsReady'
const BIDDING_PLATFORM_ORDER = ['jh', 'jl', 'baidu', 'iqiyi']

/** 登录切换账号时清空权限缓存，避免沿用上一账号的 modules/超管标记 */
export function clearPermissionCache() {
  try {
    localStorage.removeItem(LS_SUPER_ADMIN)
    localStorage.removeItem(LS_MODULES)
    localStorage.removeItem(LS_FIELDS)
    localStorage.removeItem(LS_CAN_MANAGE_ROLES)
    localStorage.removeItem(LS_PERMISSIONS_READY)
    localStorage.removeItem('allowedMenus')
  } catch (e) { /* ignore */ }
}

function markPermissionsReady() {
  try {
    localStorage.setItem(LS_PERMISSIONS_READY, '1')
  } catch (e) { /* ignore */ }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('user-permissions-ready'))
  }
}

/** /auth/me 已写入 modules/fields 后为 true；超管视为已就绪 */
export function isPermissionsReady() {
  if (isSuperAdmin()) return true
  try {
    return localStorage.getItem(LS_PERMISSIONS_READY) === '1'
  } catch (e) {
    return false
  }
}

export function getFirstAllowedBiddingPlatform() {
  return BIDDING_PLATFORM_ORDER.find(pt => hasBiddingPlatform(pt)) || null
}

export function isSuperAdmin() {
  try {
    return localStorage.getItem(LS_SUPER_ADMIN) === '1'
  } catch (e) {
    return false
  }
}

export function applyPermissionFromMe(res) {
  if (!res) return
  const superAdmin = res.role === 'admin' || res.roleCode === 'sys_admin'
  try {
    localStorage.setItem(LS_SUPER_ADMIN, superAdmin ? '1' : '0')
    if (superAdmin) {
      localStorage.removeItem(LS_MODULES)
      localStorage.removeItem(LS_FIELDS)
      localStorage.setItem(LS_CAN_MANAGE_ROLES, '1')
      markPermissionsReady()
      return
    }
    localStorage.setItem(LS_MODULES, JSON.stringify(Array.isArray(res.modules) ? res.modules : []))
    localStorage.setItem(LS_FIELDS, JSON.stringify(Array.isArray(res.fields) ? res.fields : []))
    localStorage.setItem(LS_CAN_MANAGE_ROLES, res.canManageRoles === true ? '1' : '0')
    markPermissionsReady()
  } catch (e) { /* ignore */ }
}

export function getAllowedModules() {
  if (isSuperAdmin()) return null
  try {
    const raw = localStorage.getItem(LS_MODULES)
    if (raw == null) return []
    const list = JSON.parse(raw)
    return Array.isArray(list) ? list : []
  } catch (e) {
    return []
  }
}

export function getAllowedFields() {
  if (isSuperAdmin()) return null
  try {
    const raw = localStorage.getItem(LS_FIELDS)
    if (raw == null) return []
    const list = JSON.parse(raw)
    return Array.isArray(list) ? list : []
  } catch (e) {
    return []
  }
}

export function canManageRoles() {
  try {
    return localStorage.getItem(LS_CAN_MANAGE_ROLES) === '1'
  } catch (e) {
    return false
  }
}

/** 与后端 RolePermissionSnapshot 一致：空列表=无权限；超管不限制 */
export function hasModule(code) {
  if (isSuperAdmin()) return true
  if (!code) return false
  const list = getAllowedModules()
  if (!list || list.length === 0) return false
  return list.includes(code)
}

export function hasField(code) {
  if (isSuperAdmin()) return true
  if (!code) return false
  const list = getAllowedFields()
  if (!list || list.length === 0) return false
  return list.includes(code)
}

export function hasBiddingPlatform(platformType) {
  const pt = (platformType || 'jh').toLowerCase()
  const platformCode = `bidding-delivery.${pt}`
  if (hasModule(platformCode)) return true
  if (isSuperAdmin()) return true
  const fields = getAllowedFields()
  if (!fields || !fields.length) return false
  const prefix = `${platformCode}.`
  return fields.some(f => f && f.startsWith(prefix))
}

const BIDDING_LEVEL_TABS = ['account', 'plan', 'task', 'creative']

function hasAnyBiddingTabModule(platformType) {
  const pt = (platformType || 'jh').toLowerCase()
  return BIDDING_LEVEL_TABS.some(tab => hasModule(`bidding-delivery.${pt}.${tab}`))
}

export function hasBiddingLevelTab(platformType, levelTab) {
  const pt = (platformType || 'jh').toLowerCase()
  const tab = (levelTab || 'account').toLowerCase()
  const code = `bidding-delivery.${pt}.${tab}`
  if (hasModule(code)) return true
  if (!hasBiddingPlatform(pt)) return false
  // 仅授平台模块、未单独勾选 Tab 时：与角色配置「整平台入口」一致，默认四维 Tab 可见
  const platformCode = `bidding-delivery.${pt}`
  if (hasModule(platformCode) && !hasAnyBiddingTabModule(pt)) {
    return BIDDING_LEVEL_TABS.includes(tab)
  }
  if (tab === 'account' || tab === 'summary') {
    const prefix = `${code}.`
    const fields = getAllowedFields()
    if (fields && fields.some(f => f && f.startsWith(prefix))) return true
  }
  return false
}

export function hasBiddingAccountField(platformType, fieldKey) {
  const pt = (platformType || 'jh').toLowerCase()
  return hasField(`bidding-delivery.${pt}.account.${fieldKey}`)
}

export function hasBiddingSummaryField(platformType, fieldKey) {
  const pt = (platformType || 'jh').toLowerCase()
  if (hasField(`bidding-delivery.${pt}.summary.${fieldKey}`)) return true
  if (fieldKey === 'reportTotalIncome' || fieldKey === 'reportTotalRoi') {
    return hasBiddingAccountField(pt, 'reportTotalRoi')
  }
  return false
}

/**
 * 角色配置目前只勾选「账户展示列」；计划/任务/创意同名指标继承账户字段权限。
 * 维度专属标识列始终可见；无账户同名资源的专属指标暂不拦截（角色页无法配置）。
 */
const BIDDING_ACCOUNT_METRIC_KEYS = new Set([
  'accountInfo', 'accountNickname', 'accountId', 'configOperatorName', 'qaAgentUserName',
  'deliveryAppName', 'deliveryLandingPageName', 'configBidPrice', 'configCallbackCoefficient',
  'mediaAccountName', 'channelValue', 'statDatetime', 'cashBalance', 'rebateBalance', 'giftBalance',
  'dailyBudget', 'cost', 'impressions', 'clicks', 'ctr', 'clickTransitionRatio', 'transitionCount',
  'allTransitionCost', 'clickIncome', 'billingRatio', 'activationCount', 'activationRate', 'activationCost',
  'webConversionCount', 'webConversionRate', 'webConversionCost', 'registerCount', 'registerRate',
  'registerCost', 'wakeCount', 'wakeRate', 'wakeCost', 'payCount', 'payRate', 'firstDayPayCost',
  'keyActionCount', 'keyActionRate', 'keyActionCost', 'appDeepLinkConversions', 'appDeepLinkConversionsCost',
  'appDeepLinkConversionsCVR', 'appCustomCount', 'appCustomCost', 'appCustomRate', 'ctKeyActionUv',
  'ctAppDeepLinkConversions', 'ctFeedOCPCConversionsDetail27Uv', 'ctFeedOCPCConversionsDetail25Uv',
  'adMonetizationRoi1', 'ecpm', 'publisherRealPriceOneDay', 'adsIncome', 'agdIncome', 'totalIncome',
  'incomeRoi', 'reportTotalIncome', 'reportTotalRoi', 'qualityScore', 'grossProfit', 'cpc', 'roas', 'iaa'
])

const BIDDING_DIM_TO_ACCOUNT_KEY = {
  activations: 'activationCount',
  registrations: 'registerCount',
  keyActions: 'keyActionCount',
  accountName: 'accountInfo',
  avgClickPrice: 'cpc'
}

const BIDDING_DIMENSION_IDENTITY_KEYS = {
  plan: new Set([
    'planName', 'planStatus', 'planId', 'purchaseMode', 'marketingGoal', 'planType',
    'planDailyBudget', 'operation', 'statDatetime', 'accountId', 'advertiserId'
  ]),
  task: new Set([
    'taskName', 'taskId', 'accountId', 'campaignId', 'campaignName', 'statDatetime',
    'placementDateRange', 'placementBeginDate', 'placementEndDate', 'firstExposureDate',
    'taskStatusSwitch', 'taskStatus', 'bidType', 'bidTypeLabel', 'bidMethod', 'conversionCost',
    'bid', 'configTargetConversionCost', 'advertiserId', 'operation'
  ]),
  creative: new Set([
    'creativeName', 'creativeId', 'creativeStatus', 'materialType', 'adgroupId', 'campaignId',
    'statDatetime', 'operation', 'advertiserId', 'accountId'
  ])
}

function resolveAccountFieldKey(fieldKey) {
  if (!fieldKey) return fieldKey
  return BIDDING_DIM_TO_ACCOUNT_KEY[fieldKey] || fieldKey
}

/**
 * @param {string} platformType
 * @param {string} levelTab account|plan|task|creative|summary
 * @param {string} fieldKey 列 key / API 字段名
 */
export function hasBiddingTabField(platformType, levelTab, fieldKey) {
  const pt = (platformType || 'jh').toLowerCase()
  const tab = (levelTab || 'account').toLowerCase()
  const key = fieldKey != null ? String(fieldKey).trim() : ''
  if (!key) return false
  if (tab === 'summary') return hasBiddingSummaryField(pt, key)
  if (tab === 'account') {
    if (key === 'operation') return true
    return hasBiddingAccountField(pt, resolveAccountFieldKey(key))
  }
  if (hasField(`bidding-delivery.${pt}.${tab}.${key}`)) return true
  const identity = BIDDING_DIMENSION_IDENTITY_KEYS[tab]
  if (identity && identity.has(key)) return true
  const accountKey = resolveAccountFieldKey(key)
  if (BIDDING_ACCOUNT_METRIC_KEYS.has(accountKey) || BIDDING_ACCOUNT_METRIC_KEYS.has(key)) {
    if (accountKey === 'reportTotalIncome' || accountKey === 'reportTotalRoi' || key === 'reportTotalIncome') {
      return hasBiddingAccountField(pt, 'reportTotalRoi') || hasBiddingAccountField(pt, accountKey)
    }
    return hasBiddingAccountField(pt, accountKey)
  }
  // 维度专属且角色页未建模的指标：不因账户列配置误伤
  return true
}

/**
 * 创编工作台媒体权限：
 * - 新口径：smart-create.{platformType} 模块
 * - 兼容口径：沿用竞价平台权限（bidding-delivery.{platformType}）
 * - 历史角色未配置模块/字段时：保持默认可见，避免误伤
 */
export function hasSmartCreateMedia(platformType) {
  const pt = (platformType || 'jh').toLowerCase()
  if (isSuperAdmin()) return true
  const modules = getAllowedModules()
  const fields = getAllowedFields()
  const moduleList = Array.isArray(modules) ? modules : []
  const fieldList = Array.isArray(fields) ? fields : []
  if (!moduleList.length && !fieldList.length) return true
  const hasSmartMediaModuleConfig = moduleList.some(m => typeof m === 'string' && /^smart-create\.[a-z]+$/.test(m))
  if (hasSmartMediaModuleConfig) {
    return moduleList.includes(`smart-create.${pt}`)
  }
  return hasBiddingPlatform(pt)
}

const DELIVERY_CONFIG_PREFIX = 'delivery-config.'
const DELIVERY_CONFIG_TABS = ['config', 'landing', 'template', 'changelog', 'domain']
const DELIVERY_CONFIG_MEDIA = ['jh', 'jl', 'baidu', 'iqiyi']

/** 角色是否显式配置了投放配置页内 MODULE */
export function isDeliveryConfigModuleConfigured() {
  if (isSuperAdmin()) return false
  const modules = getAllowedModules()
  if (!Array.isArray(modules) || !modules.length) return false
  return modules.some(m => typeof m === 'string' && m.startsWith(DELIVERY_CONFIG_PREFIX))
}

export function hasDeliveryConfigTab(tab) {
  if (isSuperAdmin()) return true
  const code = tab && String(tab).startsWith(DELIVERY_CONFIG_PREFIX)
    ? tab
    : `delivery-config.tab.${tab}`
  if (!isDeliveryConfigModuleConfigured()) return true
  return hasModule(code)
}

export function hasDeliveryConfigDrawer(drawer) {
  if (isSuperAdmin()) return true
  const code = drawer && String(drawer).startsWith(DELIVERY_CONFIG_PREFIX)
    ? drawer
    : `delivery-config.drawer.${drawer}`
  if (!isDeliveryConfigModuleConfigured()) return true
  return hasModule(code)
}

export function hasDeliveryConfigMedia(platformType) {
  if (isSuperAdmin()) return true
  const pt = (platformType || '').toLowerCase()
  if (!pt) return false
  if (!isDeliveryConfigModuleConfigured()) return true
  return hasModule(`delivery-config.media.${pt}`)
}

/** 完整权限：全部链接行 + 全部列表列；超管/未配页内 MODULE 视为完整 */
export function isDeliveryConfigFullAccess() {
  if (isSuperAdmin()) return true
  if (!isDeliveryConfigModuleConfigured()) return true
  return hasModule('delivery-config.full')
}

/** 无回传权限时：鲸鸿可新建；巨量/百度/爱奇艺不可新建 */
export function canCreateDeliveryConfigWithoutCallback(platformType) {
  if (hasDeliveryConfigDrawer('callback')) return true
  return (platformType || '').toLowerCase() === 'jh'
}

export function getAllowedDeliveryConfigMediaList() {
  if (isSuperAdmin() || !isDeliveryConfigModuleConfigured()) {
    return DELIVERY_CONFIG_MEDIA.slice()
  }
  return DELIVERY_CONFIG_MEDIA.filter(pt => hasDeliveryConfigMedia(pt))
}

export function getFirstAllowedDeliveryConfigTab() {
  for (const t of DELIVERY_CONFIG_TABS) {
    if (hasDeliveryConfigTab(t)) return t
  }
  return null
}

/** 侧栏菜单码是否可见：仅认叶子码；空/未就绪=无权限（与 Layout.canShowMenu 一致） */
export function hasMenuCode(code) {
  if (isSuperAdmin()) return true
  if (!code) return false
  if (!isPermissionsReady()) return false
  try {
    const raw = localStorage.getItem('allowedMenus')
    if (raw == null) return false
    const list = JSON.parse(raw)
    if (!Array.isArray(list) || list.length === 0) return false
    return list.includes(code)
  } catch (e) {
    return false
  }
}
